// src/interfaces/documentCitationTypes.ts

/**
 * Document citations for the files a conversation cites.
 * Arrive as structured data via the "document_citation" chunk type,
 * with inline <doc-cite data-ref="REF" /> tags in the response text.
 */

interface BaseDocumentCitation {
  /**
   * Reference ID matching the <doc-cite data-ref="REF" /> tag in response text.
   * Anthropic refs ("0", "1", …) may arrive as JSON numbers; Gemini refs look like "t1-c3".
   */
  ref: string;
  /** The exact text from the source document being cited */
  cited_text: string;
  /** Filename/title of the source document */
  document_title: string;
}

/**
 * Citation of a file the user attached to the conversation. One shape for
 * every provider: the BE resolves the provider's own location data to the
 * unit that was cited and sends its label, so the FE never derives a label
 * from an index it does not understand.
 *
 * location_label is the unit's own label ("Page 3" for a PDF page, the sheet
 * name for a spreadsheet, the clause number for a Word paragraph) and is
 * omitted for a unit that has none.
 */
export interface BlockLocationCitation extends BaseDocumentCitation {
  citation_type: "block_location";
  source: "document";
  /** "main" is the document open in Word; "additional" is an attached file. */
  document_scope: "main" | "additional";
  location_label?: string | null;
}

/**
 * One Catylex/source row attached to a playbook provision.
 * Status collapses verified + review_status from the raw upstream record.
 */
export interface PlaybookSource {
  label: string;
  url: string;
  quote: string;
  status: "approved" | "pending" | "unmatched";
}

/**
 * Slim playbook citation payload emitted by the BE for source="playbook"
 * search_result_location citations. See src/tools/playbook/citation.py.
 */
export interface PlaybookCitation extends BaseDocumentCitation {
  citation_type: "search_result_location";
  source: "playbook";
  breadcrumb: string[];
  importance: number | null;
  sources: PlaybookSource[];
}

/**
 * One enriched metadata concept attached to a catylex contract-header chunk.
 * Drawn from the BE allowlist (Agreement Function, Agreement Subject,
 * Home Party Role, …).
 */
export interface CatylexConcept {
  name: string;
  values: string[];
}

/**
 * Catylex citation payload emitted by the BE for source="catylex"
 * search_result_location citations. See src/tools/catylex_search/citation.py.
 *
 * chunk_type collapses the upstream sentence/contract/metadata_value trio
 * into two cases:
 *  - "sentence": pages-based citation; `pages` is set; breadcrumb is
 *    [contract_title, "Page N" | "Pages N-M"].
 *  - "contract": contract-header or folded metadata_value citation;
 *    `contract_status` and (optionally) `concepts` are set; breadcrumb
 *    is [contract_title] or [contract_title, concept_name].
 */
export interface CatylexCitation extends BaseDocumentCitation {
  citation_type: "search_result_location";
  source: "catylex search";
  chunk_type: "contract" | "sentence";
  breadcrumb: string[];
  atr_url?: string;
  contract_status?: "Active" | "Inactive";
  pages?: number[];
  concepts?: CatylexConcept[];
}

export type DocumentCitation = BlockLocationCitation | PlaybookCitation | CatylexCitation;

export const isPlaybookCitation = (c: DocumentCitation): c is PlaybookCitation =>
  c.citation_type === "search_result_location" && (c as { source?: string }).source === "playbook";

export const isCatylexCitation = (c: DocumentCitation): c is CatylexCitation =>
  c.citation_type === "search_result_location" &&
  (c as { source?: string }).source === "catylex search";
